import { useEffect, useRef } from 'react';
import type { GeometryFindingKind } from './geometryAsk.ts';

/**
 * A red geometry badge was clicked inside a preview frame.
 *
 * The badge says "click to fix", so a surface that shows one has to be able to
 * keep that promise. This is the listening half; `shared/src/shell/WarningBadge.tsx`
 * posts, and `PreviewFrame`'s `canFix` is what lets it offer the click at all.
 *
 * IT IS A HOOK BECAUSE IT WAS FORGOTTEN. The handler lived inline in
 * `routes/Canvas.tsx` and nowhere else, so the deck editor answered and the
 * Library's slide editor did not — the same badge, on the same kind of frame,
 * doing nothing at all on a library item (Alex, 2026-08-20). One importable
 * piece is what makes wiring a new surface a line rather than a thing to
 * remember, and it pairs with `canFix` so the two halves are hard to separate.
 *
 * The FINDING travels on the click rather than being looked up afterwards: the
 * live checks report on a transition, long since passed by the time anyone
 * clicks, so there is nothing left to read.
 */
export type GeometryBadgeClick = {
  /** Position among `#slides .slide` — every mounted slide can badge, so an
   *  unattributed click would aim the fix at whichever slide the studio thought
   *  was focused. -1 when the badge could not place itself. */
  idx: number;
  kind: GeometryFindingKind | null;
  detail: string;
};

export function useGeometryBadgeFix(onClick: (click: GeometryBadgeClick) => void): void {
  // Held in a ref so a surface can pass an inline arrow without re-subscribing
  // on every render — the listener is registered once, as it was inline.
  const handler = useRef(onClick);
  handler.current = onClick;
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      // Read defensively: this crosses a postMessage boundary from the deck
      // shell, as the other check handlers do.
      const d = e.data;
      if (d?.type !== 'rkh-slide-warning-click') return;
      handler.current({
        idx: typeof d.idx === 'number' ? d.idx : -1,
        kind: (d.kind as GeometryFindingKind | null) ?? null,
        detail: typeof d.detail === 'string' ? d.detail : '',
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
}
